// İşletme detay paneli (M16 T17, mockup 18 sağ panel): ad, kod (otomatik, salt okunur), tür, üst
// düğüm (grup), ülke, aktif anahtarı + pasifleştirme etki önizlemesi (C9: "N yıl verisi
// konsolideden düşer"), kaydet/vazgeç, sil (yalnız boş). Değeri olan işletmeye çocuk ekleme
// denemesinde "Değerleri taşı" diyaloğu sayfa tarafından açılır (409 CLIENT_ENTITY_HAS_VALUES).
// TODO-KPI-11: "Dahil olduğu yıllar" (başlangıç/bitiş, boş = sınırsız) — konsolide/ara toplam o yıl
// aralığında bu işletmeyi sayar; yapı değişikliği geçmiş yıl konsolidesini değiştirmez.

import { useEffect, useState } from "react";
import {
  CLIENT_ENTITY_TYPE_LABELS,
  CLIENT_ENTITY_TYPES,
  type ClientEntity,
  type ClientEntityType,
} from "@mahir/shared";
import type { EntityImpact } from "../../api/client-entities";
import { entityValidityError } from "../../lib/entity-validity-ui";
import { ConfirmDialog } from "../common/ConfirmDialog";

export interface EntityDraft {
  name: string;
  type: ClientEntityType;
  parentId: string | null;
  country: string;
  isActive: boolean;
  /** Dahil olduğu ilk yıl — metin ("" = sınırsız). */
  validFrom: string;
  /** Dahil olduğu son yıl — metin ("" = sınırsız). */
  validTo: string;
}

export interface EntityDrawerProps {
  /** null = yeni kayıt (draft'tan), dolu = düzenleme */
  entity: ClientEntity | null;
  initialDraft: EntityDraft;
  groups: ClientEntity[];
  impact: EntityImpact | null;
  canEdit: boolean;
  busy: boolean;
  error: string | null;
  onSave: (draft: EntityDraft) => void;
  onDelete: () => void;
  onCancel: () => void;
}

const field: React.CSSProperties = { display: "grid", gap: 4, marginBottom: 12 };
const label: React.CSSProperties = {
  fontSize: "0.72rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--color-text-3)",
};
const input: React.CSSProperties = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid var(--color-border)",
  background: "var(--color-surface)",
  fontSize: "0.85rem",
  color: "var(--color-text)",
};

export function EntityDrawer(props: EntityDrawerProps): JSX.Element {
  const { entity, initialDraft, groups, impact, canEdit, busy, error, onSave, onDelete, onCancel } =
    props;
  const [draft, setDraft] = useState<EntityDraft>(initialDraft);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setDraft(initialDraft);
  }, [initialDraft]);

  const isNew = entity === null;
  const deactivating = entity?.isActive === true && draft.isActive === false;
  const yearsText =
    impact && impact.years.length > 0
      ? `${impact.years[0]}–${impact.years[impact.years.length - 1]}`
      : null;
  const validityError = entityValidityError(draft.validFrom, draft.validTo);

  function submit(): void {
    if (!draft.name.trim() || validityError) return;
    if (deactivating && impact && impact.valueCount > 0) {
      setConfirmDeactivate(true);
      return;
    }
    onSave(draft);
  }

  const canDelete =
    !isNew &&
    canEdit &&
    entity?.isDefault === false &&
    impact !== null &&
    impact.valueCount === 0 &&
    impact.childCount === 0;

  return (
    <aside
      aria-label={isNew ? "Yeni işletme" : `İşletme: ${entity?.name ?? ""}`}
      className="rounded-xl p-5"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div style={{ ...label, marginBottom: 6 }}>{isNew ? "YENİ İŞLETME" : "İŞLETME"}</div>
      <h3
        className="mb-3"
        style={{ fontSize: "0.95rem", fontWeight: 700, color: "var(--color-text)" }}
      >
        {isNew ? (draft.type === "group" ? "Yeni grup" : "Yeni işletme") : entity?.name}
      </h3>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div style={field}>
          <label style={label} htmlFor="entity-name">
            Ad
          </label>
          <input
            id="entity-name"
            style={input}
            value={draft.name}
            disabled={!canEdit || busy}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            required
          />
        </div>
        {!isNew ? (
          <div style={field}>
            <span style={label}>Kod (otomatik, formüllerde kullanılmaz)</span>
            <div style={{ ...input, color: "var(--color-text-3)", fontFamily: "var(--font-mono)" }}>
              {entity?.code}
            </div>
          </div>
        ) : null}
        <div style={field}>
          <label style={label} htmlFor="entity-type">
            Tür
          </label>
          <select
            id="entity-type"
            style={input}
            value={draft.type}
            disabled={!canEdit || busy || entity?.isDefault === true}
            onChange={(e) => setDraft({ ...draft, type: e.target.value as ClientEntityType })}
          >
            {CLIENT_ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {CLIENT_ENTITY_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>
        {draft.type !== "group" ? (
          <div style={field}>
            <label style={label} htmlFor="entity-parent">
              Grup
            </label>
            <select
              id="entity-parent"
              style={input}
              value={draft.parentId ?? ""}
              disabled={!canEdit || busy}
              onChange={(e) => setDraft({ ...draft, parentId: e.target.value || null })}
            >
              <option value="">— Grupsuz (kök) —</option>
              {groups
                .filter((g) => g.id !== entity?.id)
                .map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
            </select>
          </div>
        ) : null}
        <div style={field}>
          <label style={label} htmlFor="entity-country">
            Ülke (ISO-2)
          </label>
          <input
            id="entity-country"
            style={{ ...input, width: 90, textTransform: "uppercase" }}
            value={draft.country}
            maxLength={2}
            disabled={!canEdit || busy}
            onChange={(e) => setDraft({ ...draft, country: e.target.value.toUpperCase() })}
            placeholder="TR"
          />
        </div>
        <div style={field}>
          <span style={label} id="entity-validity-label">
            Dahil olduğu yıllar (boş = sınırsız)
          </span>
          <div
            className="flex items-center gap-2"
            role="group"
            aria-labelledby="entity-validity-label"
          >
            <input
              id="entity-valid-from"
              aria-label="Başlangıç yılı"
              style={{ ...input, width: 90 }}
              value={draft.validFrom}
              inputMode="numeric"
              maxLength={4}
              placeholder="2023"
              disabled={!canEdit || busy}
              onChange={(e) => setDraft({ ...draft, validFrom: e.target.value })}
            />
            <span style={{ color: "var(--color-text-3)" }}>–</span>
            <input
              id="entity-valid-to"
              aria-label="Bitiş yılı"
              style={{ ...input, width: 90 }}
              value={draft.validTo}
              inputMode="numeric"
              maxLength={4}
              placeholder="2025"
              disabled={!canEdit || busy}
              onChange={(e) => setDraft({ ...draft, validTo: e.target.value })}
            />
          </div>
          <div className="text-xs" style={{ color: "var(--color-text-3)" }}>
            Konsolide ve ara toplam bu işletmeyi yalnız bu yıllarda sayar; geçmiş yıl konsolidesi
            yapı değişikliğinden etkilenmez.
          </div>
          {validityError ? (
            <div className="text-xs" role="alert" style={{ color: "var(--color-error)" }}>
              {validityError}
            </div>
          ) : null}
        </div>
        {!isNew ? (
          <div style={field}>
            <span style={label}>Durum</span>
            <label
              className="flex items-center gap-2 text-sm"
              style={{ color: "var(--color-text)" }}
            >
              <input
                type="checkbox"
                checked={draft.isActive}
                disabled={!canEdit || busy || entity?.isDefault === true}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
              Aktif
            </label>
            {deactivating ? (
              <div
                role="status"
                className="text-xs rounded-lg px-3 py-2"
                style={{
                  background: "var(--color-warning-bg)",
                  border: "1px solid var(--color-warning-border)",
                  color: "var(--color-warning-text)",
                }}
              >
                Pasife alırsanız: {yearsText ? `${yearsText} için ` : ""}
                {impact?.valueCount ?? 0} değer korunur, ara toplam ve konsolide değişir;
                yayınlanmış raporlar etkilenmez.
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <div
            role="alert"
            className="text-xs rounded-lg px-3 py-2 mb-3"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
            }}
          >
            {error}
          </div>
        ) : null}

        <div className="flex gap-2 justify-end mt-2">
          {canDelete ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => setConfirmDelete(true)}
              className="px-3 py-2 rounded-lg text-sm"
              style={{ color: "var(--color-error)", marginRight: "auto" }}
            >
              Sil
            </button>
          ) : null}
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: "1px solid var(--color-border)", color: "var(--color-text-2)" }}
          >
            Vazgeç
          </button>
          {canEdit ? (
            <button
              type="submit"
              disabled={busy || !draft.name.trim() || validityError !== null}
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              {busy ? "Kaydediliyor…" : "Kaydet"}
            </button>
          ) : null}
        </div>
      </form>

      <ConfirmDialog
        open={confirmDeactivate}
        title="İşletme pasife alınsın mı?"
        body={`${impact?.valueCount ?? 0} değer korunur; ${yearsText ?? "ilgili yıllar"} için ara toplam ve konsolide değişir. Yayınlanmış raporlar etkilenmez.`}
        confirmLabel="Pasife al"
        tone="neutral"
        busy={busy}
        onConfirm={() => {
          setConfirmDeactivate(false);
          onSave(draft);
        }}
        onCancel={() => setConfirmDeactivate(false)}
      />
      <ConfirmDialog
        open={confirmDelete}
        title="İşletme silinsin mi?"
        body="Bu işletmenin değeri ve alt işletmesi yok; silme geri alınamaz."
        confirmLabel="Sil"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          setConfirmDelete(false);
          onDelete();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </aside>
  );
}
